/**
 * landMaskTexture.ts
 * نسيج قناع اليابسة **مشترك** بين كل الطبقات، بقناة واحدة.
 *
 * المشكلة التي يعالجها: ستّ طبقات تقرأ landmask.png، وكل واحدة كانت ترفع نسخة نسيج
 * خاصّة بها. الصورة 8192×8192، وبصيغة RGBA تشغل **256 ميغابايت لكل نسخة** على الـ GPU.
 * بطبقتَي لون + الجسيمات + الساحل تصير ~1 غيغابايت من ذاكرة الرسوميات لقناع واحد لا
 * يتغيّر أبداً — وهو ما يُفسّر ثقل العرض: تبديل نسج (texture thrashing) وضغط على ذاكرة
 * الجهاز، لا كثرة حسابات.
 *
 * علاجان معاً:
 *  1. **نسخة واحدة** لكل سياق WebGL (الخريطة كلّها سياق واحد) — يُشترك فيها الجميع.
 *  2. **قناة واحدة** (LUMINANCE) بدل أربع: القناع رمادي أصلاً، فالقنوات الثلاث الأخرى
 *     كانت تكراراً محضاً. 256 ← 64 ميغابايت.
 *
 * المحصّلة المقيسة على الورق: ~1 غيغابايت ← 64 ميغابايت. والشيدرات لا تتغيّر إطلاقاً،
 * لأن texture2D على LUMINANCE يُعيد القيمة في .r كما كانت.
 *
 * الملكية: النسيج مملوك لهذه الوحدة لا للطبقات. لا تحذفه أي طبقة عند إزالتها — وإلّا
 * سحبت البساط من تحت البقية.
 */

import { type GLContext } from './glUtils';
import { loadRasterImage, type RasterImage } from '../../../services/rasterImageCache';

/**
 * قناع بدقّة الشبكة، بإسقاط **متساوي المستطيلات** (كنُسج القيم نفسها).
 *
 * لماذا يلزم قناع ثانٍ صغير: حلقة البيكيوبك تصنّف كل عيّنة من عيّناتها الستّ عشرة
 * يابسةً أو بحراً. كانت تفعل ذلك بقراءة القناع الكبير (8192²، 64 ميغابايت) — ستّ عشرة
 * قراءة متناثرة بتباعد خليّة شبكة (22 كم) لكل بكسل، وهي أسوأ نمط ممكن لذاكرة النسيج
 * المؤقّتة. وفوق ذلك كانت كل عيّنة تتطلّب تحويل خط عرض ⇄ مركاتور (log و tan) داخل
 * الشيدر: ستّ عشرة دالّة متسامية لكل بكسل.
 *
 * هذا القناع 2048×1024 (2 ميغابايت) وبنفس إسقاط نسيج القيم، فالعيّنة تُقرأ بإحداثيّها
 * مباشرةً بلا أي تحويل، والقراءات الستّ عشرة متجاورة داخله ⇒ إصابات ذاكرة مؤقّتة.
 * تصنيف الخليّة ثابت لا يتغيّر بالبكسل، فلا معنى لحسابه بدقّة 4 كم أصلاً.
 *
 * يُشتقّ من القناع الكبير على العميل صفّاً صفّاً (drawImage لكل صفّ مع تحويل mercV)،
 * فلا يحتاج ملفّاً إضافياً من الخادم.
 */
const GRID_MASK_W = 2048;
const GRID_MASK_H = 1024;

interface Entry {
    texture: WebGLTexture;
    ready: boolean;
    waiters: Array<() => void>;
    /** ضلع النسيج المرفوع فعلياً بالتكسل (مربّع). يلزم الشيدر لاستيفاء Catmull-Rom. */
    size: number;
}

const perContext = new WeakMap<GLContext, Entry>();
const gridPerContext = new WeakMap<GLContext, Entry>();

/** نفس صيغة mercV في الشيدرات: 0 = شمال (lat 90) .. 1 = جنوب. */
function mercV(latDeg: number): number {
    const lat = (latDeg * Math.PI) / 180;
    return (1 - Math.log(Math.tan(Math.PI * 0.25 + lat * 0.5)) / Math.PI) * 0.5;
}

/** يبني نسخة متساوية المستطيلات من القناع المركاتوري، صفّاً صفّاً. */
function toEquirect(img: RasterImage): HTMLCanvasElement | null {
    const cv = document.createElement('canvas');
    cv.width = GRID_MASK_W; cv.height = GRID_MASK_H;
    const ctx = cv.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    // نُصغّر القناع الكبير مرّة واحدة أولاً (8192² ← 2048²). بدونها كان كل صفّ من الألف
    // صفّ يقرأ شريطاً بعرض 8192 من صورة 8192² — ألف عملية على صورة ضخمة تحجز الخيط
    // الرئيس مئات المللي ثانية عند الإقلاع (ظهرت في Lighthouse كـ TBT).
    const small = document.createElement('canvas');
    small.width = GRID_MASK_W; small.height = GRID_MASK_W;
    const sctx = small.getContext('2d');
    if (!sctx) return null;
    sctx.imageSmoothingEnabled = true;
    sctx.drawImage(img, 0, 0, small.width, small.height);
    const sw = small.width, sh = small.height;
    for (let row = 0; row < GRID_MASK_H; row++) {
        // **الاتجاه حرِج**: الشيدر يقرأ هذا القناع بإحداثيّ نسيج القيم نفسه، حيث
        // v = (lat + 90) / 180 ⇒ v = 0 هو **الجنوب**. وصفّ الكانفاس 0 يصير v = 0 عند
        // الرفع (‏UNPACK_FLIP_Y_WEBGL = false أدناه)، فصفّ 0 يجب أن يكون الجنوب.
        // كان معكوساً: قِيست سيبيريا وأستراليا "بحراً" بينما ليبيا "يابسة" — تصنيف
        // مقلوب رأسياً يُفسد فصل اليابسة/البحر فتخرج الألوان بقعاً متضاربة.
        const lat = -90 + (row + 0.5) / GRID_MASK_H * 180;
        const clamped = Math.max(-85.05, Math.min(85.05, lat));
        const sy = Math.max(0, Math.min(sh - 1, Math.round(mercV(clamped) * sh)));
        ctx.drawImage(small, 0, sy, sw, 1, 0, row, GRID_MASK_W, 1);
    }
    return cv;
}

/**
 * القناع بدقّة الشبكة (متساوي المستطيلات). يُعيد نسيجاً مشتركاً؛ حتى وصول الصورة يكون
 * بكسلاً واحداً قيمته "بحر" فيبقى الفصل الساحلي معطّلاً بأمان.
 */
export function acquireGridMask(gl: GLContext, onReady: () => void): WebGLTexture | null {
    const existing = gridPerContext.get(gl);
    if (existing) {
        if (existing.ready) onReady(); else existing.waiters.push(onReady);
        return existing.texture;
    }
    const texture = gl.createTexture();
    if (!texture) return null;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 1, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, new Uint8Array([0]));
    // NEAREST: التصنيف ثنائي (يابسة/بحر) ولا معنى لاستيفائه؛ وهو أرخص أيضاً.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const entry: Entry = { texture, ready: false, waiters: [onReady], size: 1 };
    gridPerContext.set(gl, entry);

    void loadRasterImage(`${import.meta.env.BASE_URL}landmask.png`).then((img) => {
        try {
            const cv = toEquirect(img);
            if (!cv) return;
            gl.bindTexture(gl.TEXTURE_2D, texture);
            // حالة القلب عامّة في السياق ويضبطها MapLibre لنُسجه — نُثبّتها صراحةً هنا
            // كي لا يعتمد اتجاه القناع على من رسم آخر مرّة.
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, gl.LUMINANCE, gl.UNSIGNED_BYTE, cv);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            if (gl.getError() !== gl.NO_ERROR) return;
            entry.ready = true;
            for (const w of entry.waiters) w();
            entry.waiters = [];
        } catch { /* تجاهل */ }
    }).catch(() => { /* بلا قناع شبكة: الفصل الساحلي يبقى معطّلاً */ });

    return texture;
}

/**
 * ضلع قناع اليابسة المرفوع بالتكسل (‏1 قبل وصول الصورة).
 *
 * تحتاجه الشيدرات التي تستوفي القناع تكعيبياً: نافذة Catmull-Rom تُبنى بخطوة تكسل
 * واحد (1/size)، وهذا الرقم لا يمكن اشتقاقه من WebGL 1 (لا استعلام عن أبعاد نسيج).
 */
export function getLandMaskSize(gl: GLContext): number {
    return perContext.get(gl)?.size ?? 1;
}

/**
 * يُعيد النسيج المشترك فوراً (بكسل واحد مؤقّت حتى تصل الصورة)، ويستدعي onReady عند
 * جهوزه — الآن إن كان جاهزاً أصلاً. الطبقة تحتفظ براية جهوزها من هذا النداء.
 */
export function acquireLandMask(gl: GLContext, onReady: () => void): WebGLTexture | null {
    const existing = perContext.get(gl);
    if (existing) {
        if (existing.ready) onReady();
        else existing.waiters.push(onReady);
        return existing.texture;
    }

    const texture = gl.createTexture();
    if (!texture) return null;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // مؤقّت "بحر" (0) لا "يابسة": لو ظهرت الطبقة قبل وصول القناع، فالفصل الساحلي يبقى
    // معطّلاً بأمان بدل أن يعتبر العالم كلّه يابسة.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 1, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, new Uint8Array([0]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const entry: Entry = { texture, ready: false, waiters: [onReady], size: 1 };
    perContext.set(gl, entry);

    void loadRasterImage(`${import.meta.env.BASE_URL}landmask.png`).then((img) => {
        try {
            // حدّ MAX_TEXTURE_SIZE: الرفع فوقه لا يرمي استثناءً بل يترك النسيج غير مكتمل
            // فتُقرأ منه أصفار = العالم كلّه بحر. نُصغّر أوّلاً ثم نتحقّق من الخطأ.
            const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
            let source: TexImageSource = img;
            // ‏.width/.height لا .naturalWidth — الكاش يُرجع ImageBitmap (انظر rasterImageCache).
            if (max > 0 && (img.width > max || img.height > max)) {
                const s = Math.min(max / img.width, max / img.height);
                const cv = document.createElement('canvas');
                cv.width = Math.max(1, Math.floor(img.width * s));
                cv.height = Math.max(1, Math.floor(img.height * s));
                const ctx = cv.getContext('2d');
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, cv.width, cv.height);
                source = cv;
            }
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, gl.LUMINANCE, gl.UNSIGNED_BYTE, source);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            if (gl.getError() !== gl.NO_ERROR) {
                console.error('landMaskTexture: تعذّر رفع القناع');
                return;
            }
            // الحجم **المرفوع فعلاً** لا حجم الملفّ: قد يكون MAX_TEXTURE_SIZE أجبرنا على
            // التصغير أعلاه. الشيدر يبني نافذة Catmull-Rom بخطوة 1/size، فرقم خاطئ هنا
            // يزيح العيّنات عن مراكز التكسلات ويشوّه الساحل بدل أن ينعّمه.
            entry.size = source instanceof HTMLCanvasElement ? source.width : img.width;
            entry.ready = true;
            for (const w of entry.waiters) w();
            entry.waiters = [];
        } catch { /* تجاهل */ }
    }).catch(() => { /* بلا قناع: الطبقات تعمل بلا فصل ساحلي */ });

    return texture;
}
